/**
 * Écran « Notifications » (/compte/notifications) — préférences push par type.
 *
 * Gère l'abonnement du device (activer/désactiver toutes les notifs) + un toggle
 * par type (🚨 sécurité, 🧾 clôtures Z, 📥 Pennylane). Extensible : les types
 * viennent du backend (/pos/camera/push/preferences), on ajoute juste un libellé.
 * Défaut : tout activé. Réservé admin (les notifs sont métier).
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { isCurrentUserAdmin } from '../utils/auth';
import {
  pushSupported, pushEnabledServerSide, getSubscription, subscribe, unsubscribe,
  getPreferences, setPreferences,
} from '../utils/push';

const TYPE_META: Record<string, { emoji: string; label: string; desc: string }> = {
  security: { emoji: '🚨', label: 'Alertes sécurité nuit', desc: 'Présence détectée hors horaires (3h–8h).' },
  cloture_z: { emoji: '🧾', label: 'Clôtures Z', desc: 'Un Z de caisse vient d’être clôturé.' },
  pennylane: { emoji: '⚠️', label: 'Erreurs Pennylane', desc: 'Alerte seulement si un export comptable échoue.' },
  waiting: { emoji: '⏱️', label: 'Client en attente', desc: 'Quelqu’un patiente devant la caméra depuis plusieurs minutes.' },
};

type Status = 'loading' | 'unsupported' | 'unavailable' | 'off' | 'on';

export function NotificationsScreen(): React.ReactElement {
  const navigate = useNavigate();
  const admin = isCurrentUserAdmin();

  const [status, setStatus] = useState<Status>('loading');
  const [publicKey, setPublicKey] = useState<string | null>(null);
  const [endpoint, setEndpoint] = useState<string | null>(null);
  const [types, setTypes] = useState<string[]>([]);
  const [prefs, setPrefs] = useState<Record<string, boolean>>({});
  const [busy, setBusy] = useState(false);
  const [prePrompt, setPrePrompt] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  const flash = (m: string) => { setToast(m); window.setTimeout(() => setToast(null), 2500); };

  const loadPrefs = useCallback(async (ep: string) => {
    const p = await getPreferences(ep);
    if (p) { setTypes(p.types); setPrefs(p.preferences); }
  }, []);

  useEffect(() => {
    (async () => {
      if (!pushSupported) { setStatus('unsupported'); return; }
      const srv = await pushEnabledServerSide();
      if (!srv.enabled) { setStatus('unavailable'); return; }
      setPublicKey(srv.publicKey ?? null);
      const sub = await getSubscription();
      if (sub) { setEndpoint(sub.endpoint); await loadPrefs(sub.endpoint); setStatus('on'); }
      else setStatus('off');
    })();
  }, [loadPrefs]);

  const activate = useCallback(async () => {
    if (!publicKey) return;
    setPrePrompt(false);
    setBusy(true);
    try {
      const sub = await subscribe(publicKey);
      setEndpoint(sub.endpoint);
      await loadPrefs(sub.endpoint);
      setStatus('on');
      flash('Notifications activées ✓');
    } catch (e: any) {
      flash(e?.message === 'denied' ? 'Notifications bloquées dans les réglages' : 'Activation impossible');
    }
    setBusy(false);
  }, [publicKey, loadPrefs]);

  const deactivate = useCallback(async () => {
    setBusy(true);
    try {
      const sub = await getSubscription();
      if (sub) await unsubscribe(sub);
      setEndpoint(null); setPrefs({}); setStatus('off');
      flash('Notifications désactivées');
    } catch { flash('Désactivation impossible'); }
    setBusy(false);
  }, []);

  const toggle = useCallback(async (type: string) => {
    if (!endpoint) return;
    const next = { ...prefs, [type]: !prefs[type] };
    setPrefs(next); // optimiste
    const ok = await setPreferences(endpoint, { [type]: next[type] });
    if (!ok) { setPrefs(prefs); flash('Échec de la mise à jour'); }
  }, [endpoint, prefs]);

  return (
    <div style={S.wrap}>
      <header style={S.header}>
        <button style={S.back} onClick={() => navigate('/compte')}>‹</button>
        <h1 style={S.h1}>Notifications</h1>
        <span style={{ width: 32 }} />
      </header>

      <div style={S.body}>
        {status === 'loading' ? (
          <p style={S.muted}>Chargement…</p>
        ) : !admin ? (
          <p style={S.muted}>Réservé au staff.</p>
        ) : status === 'unsupported' ? (
          <p style={S.muted}>Cet appareil / navigateur ne supporte pas les notifications push.</p>
        ) : status === 'unavailable' ? (
          <p style={S.muted}>Notifications indisponibles pour cet établissement.</p>
        ) : status === 'off' ? (
          <div style={S.card}>
            <div style={S.cardIcon}>🔔</div>
            <p style={S.cardText}>Recevez les notifications importantes pour votre établissement : clôtures Z, alertes sécurité, échecs d'export comptable.</p>
            <button style={S.btnPrimary} onClick={() => setPrePrompt(true)} disabled={busy}>
              {busy ? 'Activation…' : '🔔 Activer les notifications'}
            </button>
          </div>
        ) : (
          <>
            <p style={S.sectionHint}>Notifications activées sur cet appareil. Choisissez les types :</p>
            <div style={S.list}>
              {types.map((t) => {
                const meta = TYPE_META[t] ?? { emoji: '🔔', label: t, desc: '' };
                const on = prefs[t] !== false;
                return (
                  <div key={t} style={S.row}>
                    <span style={S.rowEmoji}>{meta.emoji}</span>
                    <div style={S.rowText}>
                      <strong style={S.rowLabel}>{meta.label}</strong>
                      <span style={S.rowDesc}>{meta.desc}</span>
                    </div>
                    <button
                      role="switch" aria-checked={on} onClick={() => toggle(t)}
                      style={{ ...S.switch, ...(on ? S.switchOn : S.switchOff) }}
                    >
                      <span style={{ ...S.knob, ...(on ? S.knobOn : S.knobOff) }} />
                    </button>
                  </div>
                );
              })}
            </div>
            <button style={S.btnGhost} onClick={deactivate} disabled={busy}>
              {busy ? 'Désactivation…' : '🔕 Désactiver toutes les notifications'}
            </button>
          </>
        )}
      </div>

      {/* Pre-prompt FR AVANT la popup iOS (best practice + compense l'anglais système). */}
      {prePrompt && (
        <div style={S.modal} onClick={() => setPrePrompt(false)}>
          <div style={S.ppCard} onClick={(e) => e.stopPropagation()}>
            <div style={S.ppIcon}>🔔</div>
            <h3 style={S.ppTitle}>Activer les notifications</h3>
            <p style={S.ppBody}>
              Vous serez prévenu des événements importants de votre établissement :
              clôtures Z, alertes sécurité la nuit, échecs d'export comptable.
              Autoriser les notifications ?
            </p>
            <button style={S.btnPrimary} onClick={activate}>Autoriser</button>
            <button style={S.ppLater} onClick={() => setPrePrompt(false)}>Plus tard</button>
          </div>
        </div>
      )}

      {toast && <div style={S.toast}>{toast}</div>}
    </div>
  );
}

const S: Record<string, React.CSSProperties> = {
  wrap: { minHeight: '100dvh', background: 'var(--color-bg, #f7f8fa)', color: '#1a1a24' },
  header: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: 'max(env(safe-area-inset-top, 0px) + 16px, 47px) 16px 14px', background: 'var(--color-primary, #1464B4)' },
  back: { fontSize: 26, lineHeight: 1, color: '#fff', background: 'none', border: 'none', cursor: 'pointer', width: 32, textAlign: 'left' },
  h1: { fontSize: 18, fontWeight: 800, color: '#fff', margin: 0 },
  body: { padding: 16, maxWidth: 640, margin: '0 auto' },
  muted: { color: '#6b6b78', fontSize: 15 },
  sectionHint: { color: '#6b6b78', fontSize: 13.5, margin: '2px 0 12px' },
  card: { background: '#fff', border: '1px solid #e6e6ee', borderRadius: 16, padding: 22, textAlign: 'center' },
  cardIcon: { fontSize: 40, marginBottom: 8 },
  cardText: { fontSize: 14.5, lineHeight: 1.5, color: '#5b5b6a', margin: '0 0 18px' },
  btnPrimary: { width: '100%', padding: 14, fontSize: 16, fontWeight: 700, color: '#fff', background: '#1464B4', border: 'none', borderRadius: 12, cursor: 'pointer' },
  btnGhost: { marginTop: 18, width: '100%', padding: 13, fontSize: 14.5, fontWeight: 600, color: '#b4232f', background: '#fdecee', border: '1px solid #f3cfd3', borderRadius: 12, cursor: 'pointer' },
  list: { display: 'flex', flexDirection: 'column', gap: 8 },
  row: { display: 'flex', alignItems: 'center', gap: 12, background: '#fff', border: '1px solid #e6e6ee', borderRadius: 12, padding: '12px 14px' },
  rowEmoji: { fontSize: 22 },
  rowText: { flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' },
  rowLabel: { fontSize: 15, fontWeight: 700, color: '#2b2b38' },
  rowDesc: { fontSize: 12.5, color: '#8a8a96', marginTop: 2 },
  switch: { position: 'relative', width: 46, height: 28, borderRadius: 999, border: 'none', cursor: 'pointer', flexShrink: 0, transition: 'background .15s', padding: 0 },
  switchOn: { background: '#1464B4' },
  switchOff: { background: '#d2d2dc' },
  knob: { position: 'absolute', top: 3, width: 22, height: 22, borderRadius: '50%', background: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,.25)', transition: 'left .15s' },
  knobOn: { left: 21 },
  knobOff: { left: 3 },
  toast: { position: 'fixed', left: '50%', bottom: 24, transform: 'translateX(-50%)', background: '#1464B4', color: '#fff', padding: '10px 18px', borderRadius: 999, fontSize: 14, fontWeight: 600, zIndex: 1100, boxShadow: '0 4px 12px rgba(20,60,80,.25)' },
  modal: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, zIndex: 1200 },
  ppCard: { width: '100%', maxWidth: 360, background: '#fff', borderRadius: 18, padding: '22px 20px', textAlign: 'center', boxShadow: '0 10px 40px rgba(0,0,0,.3)' },
  ppIcon: { fontSize: 40, marginBottom: 6 },
  ppTitle: { fontSize: 18, fontWeight: 800, margin: '0 0 8px', color: '#1a1a24' },
  ppBody: { fontSize: 14, lineHeight: 1.5, color: '#5b5b6a', margin: '0 0 18px' },
  ppLater: { marginTop: 8, width: '100%', padding: '12px', fontSize: 15, fontWeight: 600, color: '#8a8a96', background: 'transparent', border: 'none', cursor: 'pointer' },
};
